import { useState, useEffect } from 'react'
import { useSyncStore } from '../syncStore'

function DataSummaryRow({ label, local, remote }: { label: string; local: number; remote: number }) {
  const diff = remote - local
  return (
    <div className="flex justify-between text-xs">
      <span className="text-gray-600">{label}</span>
      <span>
        <span className="text-gray-500">{local}</span>
        <span className="text-gray-400 mx-1">→</span>
        <span className={diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-500'}>
          {remote}
          {diff !== 0 && <span className="ml-1">({diff > 0 ? '+' : ''}{diff})</span>}
        </span>
      </span>
    </div>
  )
}

function DetailRow({
  category,
  label,
  local,
  remote,
  kind,
}: {
  category: string
  label: string
  local: string
  remote: string
  kind: 'content' | 'metadata'
}) {
  return (
    <div className="rounded border border-gray-200 bg-white p-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {category}
            </span>
            {kind === 'metadata' && (
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700">
                metadata
              </span>
            )}
          </div>
          <p className="mt-1 text-xs font-medium text-gray-700">{label}</p>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-[4rem_1fr] gap-x-2 gap-y-1 text-xs">
        <span className="font-medium text-gray-400">Local</span>
        <span className="min-w-0 break-words text-gray-700">{local}</span>
        <span className="font-medium text-gray-400">Remote</span>
        <span className="min-w-0 break-words text-gray-700">{remote}</span>
      </div>
    </div>
  )
}

function formatTimestamp(value: string | null): string {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export function SyncIndicator() {
  const {
    syncKey,
    status,
    lastSyncedAt,
    error,
    conflict,
    isConnected,
    initialize,
    setSyncKey,
    clearSync,
    getSyncUrl,
    pullFromRemote,
    resolveConflict,
    cancelConflict,
  } = useSyncStore()
  const [isOpen, setIsOpen] = useState(false)
  const [inputKey, setInputKey] = useState('')
  const [copied, setCopied] = useState(false)
  const conflictDetails = conflict?.details ?? []
  const conflictHasContentChanges = conflict?.hasContentChanges ?? (
    conflictDetails.some(detail => detail.kind === 'content') || (conflict?.differences.length ?? 0) > 0
  )
  const conflictHasMetadataOnlyChanges = conflict?.hasMetadataOnlyChanges ?? (conflictDetails.length > 0 && !conflictHasContentChanges)

  useEffect(() => {
    initialize()
  }, [initialize])

  // Auto-open when there's a conflict
  useEffect(() => {
    if (conflict) {
      setIsOpen(true)
    }
  }, [conflict])

  const handleSetKey = () => {
    if (inputKey.trim()) {
      setSyncKey(inputKey.trim())
      setInputKey('')
    }
  }

  const handleCopyUrl = () => {
    const url = getSyncUrl()
    if (url) {
      navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const statusIcon = () => {
    if (conflict) {
      return (
        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      )
    }

    switch (status) {
      case 'syncing':
        return (
          <svg className="w-5 h-5 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )
      case 'synced':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
          </svg>
        )
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
          </svg>
        )
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        )
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        title={conflict ? 'Sync conflict - action required' : syncKey ? `Synced: ${status}` : 'Set up sync'}
      >
        {statusIcon()}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => !conflict && setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 max-h-[calc(100vh-6rem)] w-[44rem] max-w-[calc(100vw-1rem)] overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
            {conflict ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-900">Data Conflict</span>
                </div>

                <div className="text-xs text-gray-600 space-y-1">
                  <p>
                    {conflictHasContentChanges
                      ? 'Local and remote both changed. Review the exact differences before choosing.'
                      : 'Local and remote counts match; only timestamps or internal metadata appear to differ.'}
                  </p>
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 rounded-lg bg-amber-50 p-2 text-amber-900">
                    <span className="font-medium">Local</span>
                    <span>{formatTimestamp(conflict.localUpdatedAt)}</span>
                    <span className="font-medium">Remote</span>
                    <span>
                      {formatTimestamp(conflict.remoteUpdatedAt)}
                      {conflict.remoteClientId && (
                        <span className="text-amber-700"> from {conflict.remoteClientId.slice(0, 8)}...</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-xs font-medium text-gray-700 border-b border-gray-200 pb-1 mb-1">
                    <span>Data</span>
                    <span>Local → Remote</span>
                  </div>
                  <DataSummaryRow
                    label="Words"
                    local={conflict.localSummary.words}
                    remote={conflict.remoteSummary.words}
                  />
                  <DataSummaryRow
                    label="Pairings"
                    local={conflict.localSummary.pairings}
                    remote={conflict.remoteSummary.pairings}
                  />
                  <DataSummaryRow
                    label="Grid Drafts"
                    local={conflict.localSummary.drafts}
                    remote={conflict.remoteSummary.drafts}
                  />
                  <DataSummaryRow
                    label="Placed Entries"
                    local={conflict.localSummary.placements}
                    remote={conflict.remoteSummary.placements}
                  />
                  <DataSummaryRow
                    label="Clues"
                    local={conflict.localSummary.clues}
                    remote={conflict.remoteSummary.clues}
                  />
                  <DataSummaryRow
                    label="Scratchpad"
                    local={conflict.localSummary.scratchpadEntries}
                    remote={conflict.remoteSummary.scratchpadEntries}
                  />
                  <DataSummaryRow
                    label="Scratchpad Canvas"
                    local={conflict.localSummary.scratchpadPlacements}
                    remote={conflict.remoteSummary.scratchpadPlacements}
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-1">
                    <span className="text-xs font-medium text-gray-700">What changed</span>
                    {conflictHasMetadataOnlyChanges && (
                      <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700">
                        metadata only
                      </span>
                    )}
                  </div>
                  {conflictDetails.length > 0 ? (
                    <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                      {conflictDetails.map((detail, index) => (
                        <DetailRow
                          key={`${detail.category}-${detail.label}-${index}`}
                          category={detail.category}
                          label={detail.label}
                          local={detail.local}
                          remote={detail.remote}
                          kind={detail.kind}
                        />
                      ))}
                    </div>
                  ) : conflict.differences.length > 0 ? (
                    <div className="space-y-1">
                      {conflict.differences.map(diff => (
                        <DataSummaryRow
                          key={diff.label}
                          label={diff.label}
                          local={diff.local}
                          remote={diff.remote}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      No content differences were found after normalizing defaults and timestamps.
                    </p>
                  )}
                </div>

                {conflictHasMetadataOnlyChanges && (
                  <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-xs text-amber-800">
                    This is usually safe to resolve with the newer timestamp, because words, pairs, clues, drafts, and placements match after normalization.
                  </div>
                )}

                {conflict.differences.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="text-xs font-medium text-gray-700 border-b border-gray-200 pb-1">
                      Count changes
                    </div>
                    {conflict.differences.map(diff => (
                      <DataSummaryRow
                        key={diff.label}
                        label={diff.label}
                        local={diff.local}
                        remote={diff.remote}
                      />
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <button
                    onClick={() => resolveConflict('merge')}
                    className="w-full text-sm px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Merge Both
                    <span className="block text-xs opacity-80">Combine local and remote data</span>
                  </button>
                  <button
                    onClick={() => resolveConflict('remote')}
                    className="w-full text-sm px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Use Remote
                    <span className="block text-xs text-gray-500">Replace local with remote data</span>
                  </button>
                  <button
                    onClick={() => resolveConflict('local')}
                    className="w-full text-sm px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Use Local
                    <span className="block text-xs text-gray-500">Keep local, overwrite remote</span>
                  </button>
                  <button
                    onClick={() => {
                      cancelConflict()
                      setIsOpen(false)
                    }}
                    className="w-full text-sm px-3 py-1.5 text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : syncKey ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Cloud Sync</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    status === 'synced' ? 'bg-green-100 text-green-700' :
                    status === 'syncing' ? 'bg-blue-100 text-blue-700' :
                    status === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {status}
                  </span>
                </div>

                {error && (
                  <p className="text-xs text-red-600 bg-red-50 p-2 rounded">{error}</p>
                )}

                <div className="text-xs text-gray-500 space-y-0.5">
                  <p>Key: {syncKey.slice(0, 8)}...</p>
                  {lastSyncedAt && (
                    <p>Last synced: {new Date(lastSyncedAt).toLocaleTimeString()}</p>
                  )}
                  <p className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`} />
                    {isConnected ? 'Live' : 'Connecting...'}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCopyUrl}
                    className="flex-1 text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                  <button
                    onClick={() => pullFromRemote()}
                    className="flex-1 text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                    disabled={status === 'syncing'}
                  >
                    Pull
                  </button>
                </div>

                <button
                  onClick={() => {
                    clearSync()
                    setIsOpen(false)
                  }}
                  className="w-full text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Set up Cloud Sync</p>
                <p className="text-xs text-gray-500">
                  Enter a sync key to save and load your data across devices.
                </p>
                <input
                  type="text"
                  value={inputKey}
                  onChange={e => setInputKey(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSetKey()}
                  placeholder="Enter sync key..."
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <button
                  onClick={handleSetKey}
                  disabled={!inputKey.trim()}
                  className="w-full text-sm px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Connect
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
